import { useEffect, useState } from "react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import "./events.css";
import { prettyDate } from "./eventUtils";

const STORAGE_KEY = "sm_admin_token";

const DEFAULT_FLOW = [
  "Opening Gratitude Blessing — We open the evening together in thanks",
  "What is Shiva Murti — The lineage, its Balinese roots, and how the healing works",
  "Meet the Instructors & Healers — An introduction to the Shiva Murti team",
  "Healing Demo — A live demonstration of the practice",
  "Spot Healings — Receive a personal healing from the Shiva Murti Sacred Healing Arts team",
  "Photo Healing & Water Blessing — Demonstrations of healing through a photograph and the blessing of water",
];
const DEFAULT_BRING = [
  "Comfortable clothing",
  "A bottle of water to be blessed",
  "An open heart",
];

const emptyForm = () => ({
  title: "Shiva Murti Healing Social",
  city_word: "",
  tagline: "",
  date: "",
  start_time: "5:30 PM",
  end_time: "7:00 PM",
  venue: "",
  address: "",
  capacity: 25,
  host: "Johnny",
  flow: DEFAULT_FLOW.join("\n"),
  bring: DEFAULT_BRING.join("\n"),
});

const toPayload = (form) => ({
  title: form.title.trim(),
  city_word: form.city_word.trim(),
  tagline: form.tagline.trim(),
  date: form.date,
  start_time: form.start_time.trim(),
  end_time: form.end_time.trim(),
  venue: form.venue.trim(),
  address: form.address.trim(),
  capacity: Number(form.capacity) || 25,
  host: form.host.trim() || "Johnny",
  flow: form.flow
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean),
  bring: form.bring
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean),
});

const fromEvent = (ev) => ({
  title: ev.title || "",
  city_word: ev.city_word || "",
  tagline: ev.tagline || "",
  date: ev.date || "",
  start_time: ev.start_time || "",
  end_time: ev.end_time || "",
  venue: ev.venue || "",
  address: ev.address || "",
  capacity: ev.capacity || 25,
  host: ev.host || "Johnny",
  flow: (ev.flow || DEFAULT_FLOW).join("\n"),
  bring: (ev.bring || DEFAULT_BRING).join("\n"),
});

const SocialsAdmin = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [password, setPassword] = useState("");
  const [authing, setAuthing] = useState(false);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const authed = Boolean(token);

  const authHeaders = () => ({ "X-Admin-Password": token });

  const loadEvents = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await api.get("/events?all=true", { headers: authHeaders() });
      setEvents(data.events || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        toast("Session expired. Please log in again.");
      } else {
        toast("Could not load events.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    if (!password) return;
    setAuthing(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      sessionStorage.setItem(STORAGE_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch (err) {
      toast("Wrong password.");
    } finally {
      setAuthing(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setToken("");
    setEvents([]);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowForm(true);
    setTimeout(() => {
      document.getElementById("admin-form")?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  };

  const openEdit = (ev) => {
    setEditingId(ev.id);
    setForm(fromEvent(ev));
    setShowForm(true);
    setTimeout(() => {
      document.getElementById("admin-form")?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.city_word || !form.date || !form.venue || !form.address) {
      toast("City, date, venue and address are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (editingId) {
        await api.put(`/events/${editingId}`, payload, { headers: authHeaders() });
        toast("Event updated.");
      } else {
        await api.post("/events", payload, { headers: authHeaders() });
        toast("Event created.");
      }
      await loadEvents();
      closeForm();
    } catch (err) {
      toast(err?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async (ev) => {
    try {
      await api.post(`/events/${ev.id}/duplicate`, {}, { headers: authHeaders() });
      toast("Event duplicated. Edit it to change the date & venue.");
      await loadEvents();
    } catch (err) {
      toast("Could not duplicate.");
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.title} — ${ev.city_word}" on ${ev.date}? This cannot be undone.`)) return;
    try {
      await api.delete(`/events/${ev.id}`, { headers: authHeaders() });
      toast("Event deleted.");
      await loadEvents();
    } catch (err) {
      toast("Could not delete.");
    }
  };

  if (!authed) {
    return (
      <div className="sm-events sm-dark sm-section" style={{ minHeight: "100vh" }} data-testid="admin-login-root">
        <SEO title="Admin — Healing Socials" description="Private admin." noindex />
        <div className="max-w-sm mx-auto">
          <p className="sm-eyebrow">Admin</p>
          <h1 className="sm-headline mt-6" style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>
            Healing Socials<br />
            <span className="sm-city">administration.</span>
          </h1>
          <form onSubmit={login} className="mt-12 space-y-6" data-testid="admin-login-form">
            <div>
              <label className="sm-label" htmlFor="pw" style={{ color: "#CDB083" }}>Password</label>
              <input
                id="pw"
                type="password"
                autoFocus
                className="sm-input mt-3"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                style={{ background: "#161210", color: "#F4ECDF", borderColor: "#CDB083" }}
              />
            </div>
            <button
              type="submit"
              className="sm-btn sm-btn-primary"
              disabled={authing}
              data-testid="admin-login-submit"
              style={{ background: "#CDB083", color: "#1E1C1A", borderColor: "#CDB083" }}
            >
              {authing ? "Checking…" : "Enter"}
              <span aria-hidden>→</span>
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="sm-events sm-light sm-section" style={{ minHeight: "100vh" }} data-testid="admin-root">
      <SEO title="Admin — Healing Socials" description="Private admin." noindex />
      <div className="max-w-[1240px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <p className="sm-eyebrow">Admin</p>
            <h1 className="sm-headline mt-4" style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)" }}>
              Healing Socials
            </h1>
          </div>
          <div className="flex gap-4">
            <button type="button" onClick={openCreate} className="sm-btn sm-btn-primary" data-testid="admin-new-event">
              New event <span aria-hidden>+</span>
            </button>
            <button type="button" onClick={logout} className="sm-btn sm-btn-ghost" data-testid="admin-logout">
              Log out
            </button>
          </div>
        </div>

        <hr className="sm-rule mt-8" />

        {loading && <p className="mt-8 sm-label" style={{ opacity: 0.6 }}>Loading…</p>}
        {!loading && events.length === 0 && (
          <p className="mt-10 text-center" style={{ opacity: 0.65 }} data-testid="admin-empty">
            No events yet. Create your first Healing Social.
          </p>
        )}
        {events.length > 0 && (
          <table className="sm-admin-table mt-6" data-testid="admin-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>City / Title</th>
                <th>Venue</th>
                <th>Capacity</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <tr key={ev.id} data-testid={`admin-row-${ev.slug}`}>
                  <td>
                    <div>{prettyDate(ev.date)}</div>
                    <div style={{ opacity: 0.6, fontSize: "0.78rem", marginTop: "0.25rem" }}>
                      {ev.start_time}{ev.end_time ? ` – ${ev.end_time}` : ""}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem" }}>
                      {ev.title} — <em style={{ color: "#B08D57" }}>{ev.city_word}</em>
                    </div>
                    <div style={{ opacity: 0.6, fontSize: "0.78rem", marginTop: "0.3rem" }}>
                      /socials/{ev.slug}
                    </div>
                  </td>
                  <td>{ev.venue}</td>
                  <td>{ev.capacity}</td>
                  <td style={{ textAlign: "right" }}>
                    <div className="flex gap-3 justify-end flex-wrap">
                      <button type="button" onClick={() => openEdit(ev)} className="sm-label" style={{ textDecoration: "underline", textUnderlineOffset: "4px" }} data-testid={`admin-edit-${ev.slug}`}>Edit</button>
                      <button type="button" onClick={() => duplicate(ev)} className="sm-label" style={{ textDecoration: "underline", textUnderlineOffset: "4px" }} data-testid={`admin-duplicate-${ev.slug}`}>Duplicate</button>
                      <button type="button" onClick={() => remove(ev)} className="sm-label" style={{ textDecoration: "underline", textUnderlineOffset: "4px", color: "#B08D57" }} data-testid={`admin-delete-${ev.slug}`}>Delete</button>
                      <a href={`/socials/${ev.slug}`} target="_blank" rel="noopener noreferrer" className="sm-label" style={{ textDecoration: "underline", textUnderlineOffset: "4px" }}>View</a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {showForm && (
          <form id="admin-form" onSubmit={save} className="mt-16 sm-light-2" style={{ padding: "3rem 2rem" }} data-testid="admin-form">
            <div className="flex items-end justify-between flex-wrap gap-4">
              <div>
                <p className="sm-eyebrow">{editingId ? "Edit event" : "New event"}</p>
                <h2 className="sm-headline mt-3" style={{ fontSize: "1.9rem" }}>
                  {editingId ? "Edit the evening" : "Add a new evening"}
                </h2>
              </div>
              <button type="button" onClick={closeForm} className="sm-label" style={{ textDecoration: "underline", textUnderlineOffset: "4px" }}>
                Cancel
              </button>
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testId="field-title" />
              <Field label="City word (italicised in gold)" value={form.city_word} onChange={(v) => setForm({ ...form, city_word: v })} testId="field-city" required />
              <Field label="Date (YYYY-MM-DD)" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testId="field-date" type="date" required />
              <Field label="Capacity" value={form.capacity} onChange={(v) => setForm({ ...form, capacity: v })} testId="field-capacity" type="number" />
              <Field label="Start time" value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} testId="field-start" placeholder="5:30 PM" />
              <Field label="End time" value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} testId="field-end" placeholder="7:00 PM" />
              <Field label="Venue" value={form.venue} onChange={(v) => setForm({ ...form, venue: v })} testId="field-venue" required />
              <Field label="Host" value={form.host} onChange={(v) => setForm({ ...form, host: v })} testId="field-host" />
              <div className="md:col-span-2">
                <Field label="Address (for directions)" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testId="field-address" required />
              </div>
              <div className="md:col-span-2">
                <Field label="Tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} testId="field-tagline" />
              </div>
              <div className="md:col-span-2">
                <TextareaField label="Flow (one per line, use em-dash — before body)" value={form.flow} onChange={(v) => setForm({ ...form, flow: v })} testId="field-flow" rows={8} />
              </div>
              <div className="md:col-span-2">
                <TextareaField label="What to bring (one per line)" value={form.bring} onChange={(v) => setForm({ ...form, bring: v })} testId="field-bring" rows={4} />
              </div>
            </div>

            <div className="mt-10 flex gap-4">
              <button type="submit" className="sm-btn sm-btn-primary" disabled={saving} data-testid="admin-save">
                {saving ? "Saving…" : editingId ? "Save changes" : "Create event"}
                <span aria-hidden>→</span>
              </button>
              <button type="button" onClick={closeForm} className="sm-btn sm-btn-ghost">Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, testId, type = "text", required = false, placeholder }) => (
  <div>
    <label className="sm-label" style={{ color: "#1E1C1A" }}>{label}{required ? " *" : ""}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="sm-input mt-3"
      placeholder={placeholder}
      data-testid={testId}
    />
  </div>
);

const TextareaField = ({ label, value, onChange, testId, rows = 5 }) => (
  <div>
    <label className="sm-label" style={{ color: "#1E1C1A" }}>{label}</label>
    <textarea
      value={value}
      rows={rows}
      onChange={(e) => onChange(e.target.value)}
      className="sm-textarea mt-3"
      data-testid={testId}
    />
  </div>
);

export default SocialsAdmin;
